import { useState, useCallback, useMemo, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { GRID_CHECKBOX_SELECTION_FIELD } from '@mui/x-data-grid';
import type {
  GridColumnResizeParams,
  GridColumnVisibilityModel,
  GridColDef,
} from '@mui/x-data-grid-pro';
import type { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro';
import type { DataGridTableId } from '../dataGridTableIds';

/** Use as `leadingGridColumnFields` when the grid has `checkboxSelection`. */
export const GRID_CHECKBOX_COLUMN_ORDER: readonly string[] = [
  GRID_CHECKBOX_SELECTION_FIELD,
];

/** Use when the grid has no leading injected columns (e.g. checkbox off). */
export const EMPTY_LEADING_GRID_COLUMNS: readonly string[] = [];

const STORAGE_PREFIX = 'orderflow:datagrid';
const SCHEMA_VERSION = 1;

/*
 * NOTE (pinned columns): Persisting pinned columns was reverted pending investigation.
 * With pinned state wired through this hook, add-row + actions column showed a bad UI
 * (actions briefly visible then gone; visibility-only persistence did not reproduce).
 * Restore the commented blocks below when the root cause is fixed.
 */

interface PersistedPreferences {
  v: number;
  columnVisibilityModel?: GridColumnVisibilityModel;
  columnWidths?: Record<string, number>;
  orderedFields?: string[];
  // pinnedColumns?: GridPinnedColumnFields;
}

function readFromStorage(key: string): PersistedPreferences | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed: PersistedPreferences = JSON.parse(raw);
    if (parsed.v !== SCHEMA_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeToStorage(key: string, prefs: PersistedPreferences): void {
  try {
    localStorage.setItem(key, JSON.stringify(prefs));
  } catch {
    // quota exceeded or storage unavailable — degrade silently
  }
}

function sanitizeVisibilityModel(
  persisted: GridColumnVisibilityModel | undefined,
  columns: readonly GridColDef[],
): GridColumnVisibilityModel | undefined {
  if (!persisted) return undefined;
  const validFields = new Set(columns.map((c) => c.field));
  const cleaned: GridColumnVisibilityModel = {};
  let hasEntries = false;
  for (const [field, visible] of Object.entries(persisted)) {
    if (validFields.has(field)) {
      cleaned[field] = visible;
      hasEntries = true;
    }
  }
  return hasEntries ? cleaned : undefined;
}

function sanitizeColumnWidths(
  persisted: Record<string, number> | undefined,
  columns: readonly GridColDef[],
): Record<string, number> | undefined {
  if (!persisted) return undefined;
  const validFields = new Set(columns.map((c) => c.field));
  const cleaned: Record<string, number> = {};
  let hasEntries = false;
  for (const [field, width] of Object.entries(persisted)) {
    if (validFields.has(field) && typeof width === 'number' && width > 0) {
      cleaned[field] = width;
      hasEntries = true;
    }
  }
  return hasEntries ? cleaned : undefined;
}

function prependLeadingColumnFields(
  ordered: string[],
  leading: readonly string[],
): string[] {
  if (!leading.length) return ordered;
  const leadingSet = new Set(leading);
  const tail = ordered.filter((f) => !leadingSet.has(f));
  return [...leading, ...tail];
}

function sanitizeOrderedFields(
  persisted: string[] | undefined,
  columns: readonly GridColDef[],
  leadingGridColumnFields: readonly string[],
): string[] | undefined {
  const userFields = columns.map((c) => c.field);
  const validFields = new Set([...userFields, ...leadingGridColumnFields]);

  const cleaned =
    persisted && persisted.length > 0
      ? persisted.filter((f) => validFields.has(f))
      : [];
  const missing = userFields.filter((f) => !cleaned.includes(f));
  let merged = [...cleaned, ...missing];
  merged = prependLeadingColumnFields(merged, leadingGridColumnFields);

  if (merged.length === 0) return undefined;
  return merged;
}

// --- Pinned persistence (disabled) ---
// function sanitizePinnedColumns(
//   persisted: GridPinnedColumnFields | undefined,
//   columns: readonly GridColDef[],
// ): GridPinnedColumnFields | undefined {
//   if (!persisted) return undefined;
//   const validFields = new Set(columns.map((c) => c.field));
//   const sanitizeSide = (fields?: string[]) =>
//     fields?.filter((f) => validFields.has(f));
//   const left = sanitizeSide(persisted.left);
//   const right = sanitizeSide(persisted.right);
//   if (!left?.length && !right?.length) return undefined;
//   const result: GridPinnedColumnFields = {};
//   if (left?.length) result.left = left;
//   if (right?.length) result.right = right;
//   return result;
// }

export interface UsePersistedDataGridColumnPreferencesOptions {
  tableId: DataGridTableId;
  columns: readonly GridColDef[];
  apiRef: MutableRefObject<GridApiPro>;
  defaultVisibilityModel?: GridColumnVisibilityModel;
  /**
   * Injected column fields that must stay at the start of `orderedFields` when
   * rehydrating from storage (e.g. `GRID_CHECKBOX_COLUMN_ORDER` when using
   * `checkboxSelection`).
   */
  leadingGridColumnFields?: readonly string[];
  // defaultPinnedColumns?: GridPinnedColumnFields;
}

/**
 * Persists column visibility, widths, and column order to localStorage.
 * Pinned columns are not persisted (pins disabled — see NOTE above).
 */
export function usePersistedDataGridColumnPreferences({
  tableId,
  columns,
  apiRef,
  defaultVisibilityModel = {},
  leadingGridColumnFields = EMPTY_LEADING_GRID_COLUMNS,
  // defaultPinnedColumns,
}: UsePersistedDataGridColumnPreferencesOptions) {
  const storageKey = `${STORAGE_PREFIX}:${tableId}`;

  const stored = useMemo(() => readFromStorage(storageKey), [storageKey]);

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(
      () =>
        sanitizeVisibilityModel(stored?.columnVisibilityModel, columns) ??
        defaultVisibilityModel,
    );

  // const [pinnedColumns, setPinnedColumns] = useState<
  //   GridPinnedColumnFields | undefined
  // >(
  //   () =>
  //     sanitizePinnedColumns(stored?.pinnedColumns, columns) ??
  //     defaultPinnedColumns,
  // );

  const persist = useCallback(
    (patch: Partial<PersistedPreferences>) => {
      const current = readFromStorage(storageKey) ?? {
        v: SCHEMA_VERSION,
      };
      writeToStorage(storageKey, { ...current, ...patch, v: SCHEMA_VERSION });
    },
    [storageKey],
  );

  const onColumnVisibilityModelChange = useCallback(
    (newModel: GridColumnVisibilityModel) => {
      setColumnVisibilityModel(newModel);
      persist({ columnVisibilityModel: newModel });
    },
    [persist],
  );

  // Track latest order and widths in refs so applyPersistedPreferences
  // always reads the most up-to-date values without extra re-renders.
  const orderedFieldsRef = useRef<string[] | undefined>(
    sanitizeOrderedFields(
      stored?.orderedFields,
      columns,
      leadingGridColumnFields,
    ),
  );

  const columnWidthsRef = useRef<Record<string, number> | undefined>(
    sanitizeColumnWidths(stored?.columnWidths, columns),
  );

  const onColumnWidthChange = useCallback(
    (params: GridColumnResizeParams) => {
      const current = readFromStorage(storageKey);
      const columnWidths = { ...(current?.columnWidths ?? {}) };
      columnWidths[params.colDef.field] = params.width;
      columnWidthsRef.current = columnWidths;
      persist({ columnWidths });
    },
    [storageKey, persist],
  );

  const onColumnOrderChange = useCallback(() => {
    const orderedFields = apiRef.current.getAllColumns().map((c) => c.field);
    orderedFieldsRef.current = orderedFields;
    persist({ orderedFields });
  }, [apiRef, persist]);

  /**
   * Reorders columns and applies persisted widths to the given column defs.
   *
   * Use this when `columns` has `useMemo` dependencies that cause recomputation
   * (e.g. `rowModesModel`). The returned array has the user's saved column order
   * and widths baked in, so `initialState.columns` is no longer needed on the
   * `<DataGridPro>`.
   */
  const applyPersistedPreferences = useCallback(
    (cols: readonly GridColDef[]): GridColDef[] => {
      const savedOrder = orderedFieldsRef.current;
      const savedWidths = columnWidthsRef.current;

      const withWidths = cols.map((col) => {
        const w = savedWidths?.[col.field];
        if (w != null) {
          const { flex: _flex, ...rest } = col;
          return { ...rest, width: w } as GridColDef;
        }
        return col;
      });

      if (!savedOrder?.length) return withWidths;

      const byField = new Map(withWidths.map((c) => [c.field, c]));
      const result: GridColDef[] = [];
      const leadingSet = new Set(leadingGridColumnFields);

      for (const field of leadingGridColumnFields) {
        const col = byField.get(field);
        if (col) {
          result.push(col);
          byField.delete(field);
        }
      }

      for (const field of savedOrder) {
        if (leadingSet.has(field)) continue;
        const col = byField.get(field);
        if (col) {
          result.push(col);
          byField.delete(field);
        }
      }

      byField.forEach((col) => {
        result.push(col);
      });

      return result;
    },
    [leadingGridColumnFields],
  );

  // const onPinnedColumnsChange = useCallback(
  //   (newPinned: GridPinnedColumnFields) => {
  //     setPinnedColumns(newPinned);
  //     persist({ pinnedColumns: newPinned });
  //   },
  //   [persist],
  // );

  return {
    columnVisibilityModel,
    onColumnVisibilityModelChange,
    onColumnWidthChange,
    onColumnOrderChange,
    orderedFieldsRef,
    columnWidthsRef,
    applyPersistedPreferences,
    // pinnedColumns,
    // onPinnedColumnsChange,
  };
}
